import type { AdminUserWithClaims } from "@hellocashflow/domain";
import type { ReactNode } from "react";

import { formatDateTime } from "@/pages/adminAccessAuditHelpers";
import { VNextPill } from "@/vnext/primitives";

interface AdminAccessAuditResultsTableProps {
  claimKey: string;
  users: readonly AdminUserWithClaims[];
}

const getFirstPresentString = (...values: Array<string | null | undefined>) => {
  for (const value of values) {
    if (typeof value === "string" && value.trim().length > 0) {
      return value;
    }
  }

  return null;
};

const formatClaimValue = (value: unknown): string => {
  if (typeof value === "string") {
    return value;
  }
  if (
    typeof value === "number" ||
    typeof value === "boolean" ||
    typeof value === "bigint"
  ) {
    return String(value);
  }
  if (value === null || value === undefined) {
    return String(value);
  }

  return JSON.stringify(value);
};

const renderPillList = (items: ReactNode[]) => {
  if (items.length === 0) {
    return <span className="admin-access-audit__muted">None</span>;
  }

  return <div className="admin-access-audit__pill-list">{items}</div>;
};

const renderClaims = (
  claims: AdminUserWithClaims["firebaseUser"]["customClaims"],
) => {
  const entries = Object.entries(claims);

  return renderPillList(
    entries.map(([key, value]) => (
      <VNextPill key={key} tone="accent">
        {`${key}: ${formatClaimValue(value)}`}
      </VNextPill>
    )),
  );
};

const renderAuthProviders = (user: AdminUserWithClaims) => {
  const providers = user.appUser?.authProviders;
  if (!providers || providers.length === 0) {
    return <span className="admin-access-audit__muted">No providers</span>;
  }

  return renderPillList(
    providers.map((provider) => (
      <VNextPill key={`${user.firebaseUser.uid}-${provider}`} tone="default">
        {provider}
      </VNextPill>
    )),
  );
};

const getClaimState = (user: AdminUserWithClaims, claimKey: string) => {
  const claims = user.firebaseUser.customClaims;
  if (!Object.prototype.hasOwnProperty.call(claims, claimKey)) {
    return "missing";
  }

  return claims[claimKey] === true ? "enabled" : "disabled";
};

const renderIdentityStatus = (user: AdminUserWithClaims, claimKey: string) => (
  <div className="admin-access-audit__pill-list">
    <VNextPill
      tone={getClaimState(user, claimKey) === "enabled" ? "success" : "warning"}
    >
      {getClaimState(user, claimKey) === "enabled"
        ? `${claimKey} claim on`
        : `${claimKey} claim off`}
    </VNextPill>
    <VNextPill tone={user.firebaseUser.emailVerified ? "success" : "warning"}>
      {user.firebaseUser.emailVerified ? "Verified" : "Unverified"}
    </VNextPill>
    <VNextPill tone={user.firebaseUser.disabled ? "danger" : "accent"}>
      {user.firebaseUser.disabled ? "Disabled" : "Active"}
    </VNextPill>
  </div>
);

export function AdminAccessAuditResultsTable({
  claimKey,
  users,
}: AdminAccessAuditResultsTableProps) {
  if (users.length === 0) {
    return (
      <div className="vnext-empty">
        No admin users were found for the current claim scan.
      </div>
    );
  }

  return (
    <div className="vnext-scroll-panel">
      <table className="vnext-table">
        <thead>
          <tr>
            <th>Admin identity</th>
            <th>Firebase account</th>
            <th>Application record</th>
            <th>Custom claims</th>
          </tr>
        </thead>
        <tbody>
          {users.map((user) => (
            <tr key={user.firebaseUser.uid}>
              <td>
                <div className="admin-access-audit__cell-stack">
                  <strong>
                    {getFirstPresentString(
                      user.firebaseUser.displayName,
                      user.appUser?.name,
                      "Unnamed admin",
                    ) ?? "Unnamed admin"}
                  </strong>
                  <span className="admin-access-audit__muted">
                    {getFirstPresentString(
                      user.firebaseUser.email,
                      user.appUser?.email,
                      "Email unavailable",
                    ) ?? "Email unavailable"}
                  </span>
                  <code>{`UID: ${user.firebaseUser.uid}`}</code>
                  {renderIdentityStatus(user, claimKey)}
                </div>
              </td>
              <td>
                <div className="admin-access-audit__cell-stack">
                  <span>{`Created: ${formatDateTime(
                    user.firebaseUser.creationTime,
                  )}`}</span>
                  <span>{`Last sign-in: ${formatDateTime(
                    user.firebaseUser.lastSignInTime,
                  )}`}</span>
                  <span>
                    {`Phone: ${user.firebaseUser.phoneNumber ?? "Not linked"}`}
                  </span>
                  <span>
                    {user.firebaseUser.photoURL
                      ? "Profile photo present"
                      : "No profile photo"}
                  </span>
                </div>
              </td>
              <td>
                <div className="admin-access-audit__cell-stack">
                  {user.appUser ? (
                    <>
                      <span>{`App User ID: ${user.appUser.id}`}</span>
                      <span>{user.appUser.email ?? "No app email"}</span>
                      <span>
                        {`Display: ${user.appUser.name ?? "No display name"}`}
                      </span>
                      <span>
                        {`Stripe ID: ${
                          getFirstPresentString(user.appUser.stripeId) ??
                          "Not linked"
                        }`}
                      </span>
                      <span>{`Login count: ${
                        user.appUser.login_count ?? 0
                      }`}</span>
                      <span>{`Last login: ${formatDateTime(
                        user.appUser.last_login_at,
                      )}`}</span>
                      <span>{`Created: ${formatDateTime(
                        user.appUser.created_at,
                      )}`}</span>
                      <span>
                        {`Viewed update log: ${
                          user.appUser.viewedUpdateLog ? "Yes" : "No"
                        }`}
                      </span>
                      {renderAuthProviders(user)}
                    </>
                  ) : (
                    <span className="admin-access-audit__muted">
                      No application record linked to this Firebase admin.
                    </span>
                  )}
                </div>
              </td>
              <td>{renderClaims(user.firebaseUser.customClaims)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
